/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LogDialogConfig, logDialogRoute } from '@gh/common-internal-shared';

import {
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService } from '@gh/core-ui';
import { cursorTracker, GridRowEvent, newGridPaginationCursor } from '@gh/core-ui-grid';
import { currentDate, openInNewWindow, toString } from '@gh/core-util';

import { XeroIssueLogBusinessService, XeroIssueLogModel } from '../../resources';
import { XeroIssueLogGridDefinitionService } from './xero-issue-log-grid-definition.service';
import { padStart } from 'lodash';

@Component({
  standalone: false,
  selector: 'gh-xero-issue-log-list',
  templateUrl: 'xero-issue-log-list.component.html',
  styleUrls: ['xero-issue-log-list.component.scss'],
  viewProviders: [XeroIssueLogGridDefinitionService],
})
export class XeroIssueLogListComponent implements OnInit {
  dataSource: SlicedDataSource<XeroIssueLogModel>;
  form: FilterableFormState<any>;
  cursor = newGridPaginationCursor();
  defaultStartDate = currentDate();
  defaultStartDateStr = `${currentDate().getFullYear()}/${currentDate().getMonth() + 1}/${currentDate().getDate()}`;
  initialFilter = {
    searchString: '',
    startDate: new Date(this.defaultStartDate.setDate(this.defaultStartDate.getDate() - 14))
      .toISOString().split('T')[0],
    endDate: new Date(this.defaultStartDateStr),
  };

  constructor(
    private businessService: XeroIssueLogBusinessService,
    public gridDefinition: XeroIssueLogGridDefinitionService,
    private dialogService: DialogService,
    private formStateBuilder: FormStateBuilder,
    private routeData: RouteDataService,
    private route: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);

    this.gridDefinition.bind(gridLayout);
    this.load();
  }

  onRowClicked(event: GridRowEvent<XeroIssueLogModel>): void {
    this.dialogService.openWithInput(logDialogRoute, <LogDialogConfig>{ content: event.data.resultMessage });
  }

  onOpenXero(log: XeroIssueLogModel): void {
    openInNewWindow(log.xeroInvoiceUrl);
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onReset(): void {
    this.form.setValue({
                         searchString: this.form.value.searchString,
                         startDate: null,
                         endDate: null,
                       });
    this.form.emitFilter();
  }

  stringifyDateComponent(n: number): string {
    return padStart(toString(n), 2, '0');
  }

  private load(): void {
    this.dataSource = this.businessService.findAllAsDataSource(this.initialFilter);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: this.initialFilter,
      children: {
        searchString: void 0,
        startDate: void 0,
        endDate: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      const endDate = new Date(filter.endDate);
      const startDate = new Date(filter.startDate);
      const fixedFilter = {
        searchString: filter.searchString || '',
        startDate: filter.startDate ? `${[startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate()].map(this.stringifyDateComponent).join('-')}T00:00:00` : null,
        endDate: filter.endDate ? `${[endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate()].map(this.stringifyDateComponent).join('-')}T23:59:59` : null,
      };
      this.dataSource = this.businessService.findAllAsDataSource(fixedFilter);
    });
  }
}
